import { useMemo, useRef } from "react"
import { useFilterQueryParams } from "../../hooks/filter_query_params"
import { useMapViewData } from "../../hooks/map_view_data"
import { buildDisplayVolumeFn, filterRoutesByThreshold } from "../../utils/flowUtils"

export function useFlowDetails() {
  const { campusFlow, flowBuilding, flowThreshold, days, hour } = useFilterQueryParams()
  const { campusFlowData, resources } = useMapViewData()
  const { getBuilding } = resources

  // Stabilize getBuilding ref to avoid defeating useMemo
  const getBuildingRef = useRef(getBuilding)
  getBuildingRef.current = getBuilding

  return useMemo(() => {
    if (!campusFlow || !flowBuilding) return null

    const getName = id => getBuildingRef.current?.(id)?.name || id

    if (!campusFlowData?.routes?.length) {
      return {
        buildingName: getName(flowBuilding),
        inflow: 0,
        outflow: 0,
        topArrivals: [],
        topDepartures: [],
      }
    }

    const displayVolumeFn = buildDisplayVolumeFn(days, hour)
    const getVolume = route => displayVolumeFn ? displayVolumeFn(route) : (route.semester_data?.volume ?? 0)
    const filteredRoutes = filterRoutesByThreshold(campusFlowData.routes, flowThreshold, flowBuilding, displayVolumeFn, days, hour)

    let inflow = 0
    let outflow = 0
    const arrivalMap = {}
    const departureMap = {}

    for (const route of filteredRoutes) {
      const vol = getVolume(route)
      if (route.end_building_id === flowBuilding) {
        inflow += vol
        arrivalMap[route.start_building_id] = (arrivalMap[route.start_building_id] || 0) + vol
      }
      if (route.start_building_id === flowBuilding) {
        outflow += vol
        departureMap[route.end_building_id] = (departureMap[route.end_building_id] || 0) + vol
      }
    }

    const sortByVolume = map =>
      Object.entries(map)
        .map(([id, volume]) => ({ name: getName(id), volume }))
        .sort((a, b) => b.volume - a.volume)
        .slice(0, 3)

    return {
      buildingName: getName(flowBuilding),
      inflow,
      outflow,
      topArrivals: sortByVolume(arrivalMap),
      topDepartures: sortByVolume(departureMap),
    }
  }, [campusFlow, flowBuilding, flowThreshold, campusFlowData, days, hour])
}
